import React, { useState, useCallback } from 'react';
import { Upload, AlertCircle, CheckCircle2, X, FileWarning, FileText } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';

/**
 * BulkMediaUploader - Drag-and-drop bulk upload with file size warnings
 */
const BulkMediaUploader = ({ onUploadComplete, onClose }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const processFiles = (fileList) => {
    const processedFiles = fileList.map(file => ({
      file,
      id: Math.random().toString(36),
      name: file.name,
      size: file.size,
      sizeKB: Math.round(file.size / 1024),
      sizeMB: (file.size / (1024 * 1024)).toFixed(2),
      type: file.type,
      isLarge: file.size > 500 * 1024, // Over 500KB
      isHuge: file.size > 5 * 1024 * 1024, // Over 5MB
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setFiles(prev => [...prev, ...processedFiles]);
  };

  const removeFile = (fileId) => {
    setFiles(prev => {
      const removed = prev.find(f => f.id === fileId);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Helper to get fresh token
    const getFreshToken = async () => {
      let token = sessionStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (token && refreshToken) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const exp = payload.exp * 1000;
          if (exp - Date.now() < 30000) {
            const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken })
            });
            if (response.ok) {
              const data = await response.json();
              if (data.access_token) {
                sessionStorage.setItem('token', data.access_token);
                token = data.access_token;
              }
            }
          }
        } catch (e) { /* ignore */ }
      }
      return token;
    };
    
    // Upload with retry on 401
    const uploadWithRetry = async (formData, isRetry = false) => {
      const token = isRetry ? await getFreshToken() : sessionStorage.getItem('token');
      // Skip server optimization for faster uploads
      const response = await fetch(`${BACKEND_URL}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.status === 401 && !isRetry) {
        return uploadWithRetry(formData, true);
      }
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
      return response;
    };
    
    try {
      let completed = 0;
      
      for (const fileData of files) {
        const formData = new FormData();
        formData.append('file', fileData.file);
        
        await uploadWithRetry(formData);
        
        completed++;
        setUploadProgress(Math.round((completed / files.length) * 100));
      }
      
      // Clean up previews
      files.forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      
      if (onUploadComplete) onUploadComplete();
      if (onClose) onClose();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Some files failed to upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
  const largeFilesCount = files.filter(f => f.isLarge).length;

  return (
    <div className="space-y-4">
      {/* Pre-Upload Guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 text-sm mb-1">
              Image Optimization Tips
            </h4>
            <p className="text-xs text-blue-800 leading-relaxed">
              For best performance, keep image files <strong>below 500 KB</strong>. 
              Ideal size is <strong>below 150 KB</strong> for fast mobile loading. 
              Recommended formats: <strong>JPEG</strong> or <strong>WebP</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Drag-and-Drop Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-700 mb-1">
          Drag and drop files here
        </p>
        <p className="text-xs text-gray-500 mb-4">
          or click the button below to browse
        </p>
        <input
          ref={input => input}
          type="file"
          multiple
          accept="image/*,video/*,application/pdf"
          onChange={handleFileSelect}
          className="hidden"
          id="bulk-file-input"
        />
        <label htmlFor="bulk-file-input">
          <Button asChild variant="outline" size="sm">
            <span>Browse Files</span>
          </Button>
        </label>
      </div>

      {/* Selected Files List */}
      {files.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">
                Selected Files ({files.length})
              </h3>
              <div className="text-xs text-gray-600">
                Total: {totalSizeMB} MB
              </div>
            </div>

            {/* Warning for large files */}
            {largeFilesCount > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
                <div className="flex items-start gap-2">
                  <FileWarning className="h-4 w-4 text-yellow-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-yellow-900">
                      {largeFilesCount} Large File{largeFilesCount > 1 ? 's' : ''} Detected
                    </p>
                    <p className="text-xs text-yellow-800 mt-1">
                      Files over 500 KB may slow down page loading. Consider compressing them for better performance.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {files.map((fileData) => (
                <div
                  key={fileData.id}
                  className={`flex items-center gap-3 p-2 rounded border ${
                    fileData.isHuge
                      ? 'border-red-200 bg-red-50'
                      : fileData.isLarge
                      ? 'border-yellow-200 bg-yellow-50'
                      : 'border-gray-200'
                  }`}
                >
                  {/* Preview */}
                  {fileData.preview ? (
                    <img
                      src={fileData.preview}
                      alt={fileData.name}
                      className="w-12 h-12 object-cover rounded"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      <FileText className="h-6 w-6 text-gray-400" />
                    </div>
                  )}

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileData.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">
                        {fileData.sizeKB} KB
                      </span>
                      {fileData.isHuge && (
                        <Badge variant="destructive" className="text-xs">
                          Very Large ({fileData.sizeMB} MB)
                        </Badge>
                      )}
                      {fileData.isLarge && !fileData.isHuge && (
                        <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-700">
                          Large
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(fileData.id)}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4 space-y-2">
                <Progress value={uploadProgress} />
                <p className="text-xs text-center text-gray-600">
                  Uploading... {uploadProgress}%
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  files.forEach(f => {
                    if (f.preview) URL.revokeObjectURL(f.preview);
                  });
                  setFiles([]);
                  if (onClose) onClose();
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload {files.length} File{files.length > 1 ? 's' : ''}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkMediaUploader;
